/**
 * @migrated
 * Represents flight data that may include multiple flights with varying prices.
 *
 * Flight structure details:
 * - Each flight can contain multiple groups.
 * - Each group can include one or more schedules.
 *
 * Flight type specifications:
 * - **Round-trip:** Always consists of exactly 2 groups. (onward and return)
 * - **Multi-city:** Contains multiple groups, each having exactly 1 schedule.
 * - **One-way:** Contains a single group, which may include multiple schedules.
 */

import {
	Box,
	Button,
	Icons,
	Divider,
	Inline,
	Stack,
	useTimeout,
	Col,
	Grid,
	Badge,
	Text,
	Time,
	Spinner,
	Select,
	Alert,
	TableHeaderDataCell,
	TableDataCell,
	joinAttributes,
	DeferRender,
	Table,
} from "@sembark-travel/ui-base"
import {
	dateToUTCString,
	startOf,
	endOf,
	getDiff,
	humanizeDuration,
	isSameOrAfter,
	parseDate,
	getTimezoneOffset,
	subtractUnit,
	convertLocalToUtcAccordingToTimezone,
} from "@sembark-travel/datetime-utils"
import { useXHR } from "@sembark-travel/xhr"
import React, { useCallback, useMemo, useRef, useEffect, useState } from "react"
import { Required } from "utility-types"
import * as Validator from "yup"
import { type TChildrenArray } from "../Tourists"
import {
	addLabelToAirport,
	getChildrenDistribution,
	IFlightPricing,
	IFlightSchedules,
	IFlightAirPort,
	TFlightClass,
	TFlightFormFillMode,
	TFlightGroupType,
	TFlightType,
	transformFlightsFormDataToPreviewData,
	FLIGHT_CATEGORY_CLASSES,
	ADDITIONAL_FLIGHT_OPTIONS,
	DEFAULT_FLIGHT_CLASS,
	FLIGHT_TYPE,
	computePerPersonDistribution,
	getFlightPerPersonTotal,
	getFlightNumbers,
	FLIGHT_INFANT_AGE_LIMIT,
	FLIGHT_CHILD_AGE_LIMIT,
	TFlightPaxType,
	TFlight,
} from "./store"
import {
	Form,
	TextInputField,
	SelectField,
	DatePickerField,
	validateFormValues,
	arrayMutators,
	FieldArray,
	GetFieldValue,
	useFormState,
	EmptyNumberValidator,
	MoneyInputField,
	useFieldValue,
	TextAreaInputField,
	useForm,
	withServerErrors,
	SubmissionError,
	getFormValueForKey,
	InlineSelectInputField,
	CheckboxInputField,
} from "@sembark-travel/ui-form"
import { TTripDestination } from "../TripDestinations"
import { useCurrenciesCodesOfTenant } from "../Currencies"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { SectionIcon } from "../Trips/Quotes"
import { TWorldAirline, TWorldAirport } from "../World"
import {
	SelectAdditionalFlightInfo,
	SelectAirlines,
	SelectAirports,
} from "./List"
import {
	formatMoneyByDecimal,
	moneyParseByDecimal,
	multipleMoneyBy,
} from "@sembark-travel/money"

type TAdditionalOptions = {
	id: number
	value: string
	type: string
}
export interface IFlightServicesParams {
	flight_form_fill_mode?: TFlightFormFillMode
	flight_type?: TFlightType
	flights?: Array<{
		pricing: IFlightPricing
		groups: Array<{
			type: TFlightGroupType
			schedules: Array<{
				source?: TWorldAirport // from departure in auto airport id of source
				destination?: TWorldAirport // to arrival
				departs_at?: string
				arrives_at?: string
				departs_at_local?: Date | string | undefined
				arrives_at_local?: Date | string | undefined
				airline?: {
					id?: number | string //we get the id when there is flight api call and we don't get that id when we manually search for flight
					number_plate_formatted: string
					number_plate: string
					airline: TWorldAirline
				}
				departure_terminal?: number | string
				arrival_terminal?: number | string
			}>
			remarks?: string
			additional_options?: Array<{ id: number; value: string }>
			info?: {
				adult_check_in_baggage?: TAdditionalOptions
				adult_hand_baggage?: TAdditionalOptions
				infant_check_in_baggage?: TAdditionalOptions
				infant_hand_baggage?: TAdditionalOptions
				fare_type?: TAdditionalOptions
				meal_preference?: TAdditionalOptions
				category_class: TFlightClass
			}
		}>
	}>
	flight_preview?: 0 | 1
}
interface IFlightServicesFormProps {
	initialValues?: IFlightServicesParams
	onChange: (data: IFlightServicesParams) => void
	bookingFrom?: string
	bookingTo?: string
	no_of_adults?: number
	children?: TChildrenArray
	tripDestinations: Array<TTripDestination>
	flightPreview?: 1 | 0
}

export interface IFlightSearchParams {
	flight_type: TFlightType
	onward_flight_details: Array<{
		departure_date: Date
		flight_number?: IFlightSchedules["flight"]
		departure_airport?: TWorldAirport
	}>
	return_flight_details?: Array<{
		departure_date: Date
		flight_number?: IFlightSchedules["flight"]
		departure_airport?: TWorldAirport
	}>
}

type TGroup = TFlight["groups"][number]

const validationSchema = Validator.object().shape({
	flights: Validator.array().of(
		Validator.object().shape({
			pricing: Validator.object().shape({
				no_of_adults: EmptyNumberValidator()
					.typeError("Number of persons should be a positive integer")
					.required("Please provide number adult passengers")
					.positive("Number of persons should be a positive integer")
					.integer("Number of persons should be a positive integer"),
				children: EmptyNumberValidator()
					.typeError("Number of persons should be a non-negative integer")
					.integer("Number of children should be a non-negative integer"),
				infants: EmptyNumberValidator()
					.typeError("Number of infants should be a non-negative integer")
					.integer("Number of infants should be a non-negative integer"),
				calculated_price: EmptyNumberValidator()
					.typeError("Price should be a positive number")
					.required("Please provide the cost price")
					.min(0, "Price should be a non-negative number"),
				given_price: EmptyNumberValidator()
					.typeError("Price should be a positive number")
					.required("Please provide the given price")
					.min(0, "Price should be a non-negative number"),
				per_person_cost_price: Validator.object()
					.nullable()
					.optional()
					.shape({
						adult: EmptyNumberValidator().min(
							0,
							"Price should be a positive number"
						),
						child: EmptyNumberValidator().min(
							0,
							"Price should be a positive number"
						),
						infant: EmptyNumberValidator().min(
							0,
							"Price should be a positive number"
						),
					}),
				per_person_given_price: Validator.object()
					.nullable()
					.optional()
					.shape({
						adult: EmptyNumberValidator().min(
							0,
							"Price should be a positive number"
						),
						child: EmptyNumberValidator().min(
							0,
							"Price should be a positive number"
						),
						infant: EmptyNumberValidator().min(
							0,
							"Price should be a positive number"
						),
					}),
			}),
			groups: Validator.array().of(
				Validator.object().shape({
					schedules: Validator.array().of(
						Validator.object().shape({
							source: Validator.object().required(
								"Please select the source/departure airport"
							),
							destination: Validator.object().required(
								"Please select the destination/arrival airport"
							),
							departs_at_local: Validator.date().required(
								"Please select the departure date/time"
							),
							arrives_at_local: Validator.date()
								.required("Please select the arrival date/time")
								.when(["departs_at_local", "source", "destination"], ((
									[departs_at_local, source, destination]: [
										string | undefined,
										TWorldAirport,
										TWorldAirport,
									],
									schema: Validator.AnyObjectSchema
								) => {
									if (!departs_at_local) {
										return schema
									}
									const departUtc = convertLocalToUtcAccordingToTimezone(
										departs_at_local,
										source?.timezone?.name
									)

									return schema.test({
										name: `arrival_after_departure`,
										test: (value: string | null | undefined) => {
											if (!value) return true
											const arrivalUtc = convertLocalToUtcAccordingToTimezone(
												value,
												destination?.timezone?.name
											)
											const departDate = new Date(departUtc)
											const arrivalDate = new Date(arrivalUtc)

											if (arrivalDate.getTime() < departDate.getTime()) {
												return false
											}
											return true
										},
										message: `Arrival time must be after departure time.`,
									})
								}) as never),
							airline: Validator.object().required(
								"Please select the provided airline"
							),
						})
					),
					remarks: Validator.string().max(
						500,
						"Maximum 500 characters allowed"
					),
					info: Validator.object().shape({
						category_class: Validator.string().required(
							"Please select the category class"
						),
					}),
				})
			),
		})
	),
})

const validate = validateFormValues(validationSchema)

const quoteFlightPricingInitialValues: IFlightPricing = {
	currency: "INR",
	calculated_price: undefined,
	given_price: undefined,
	comments: "",
	no_of_adults: 0,
	children: 0,
	infants: 0,
	cost_per_person_enabled: 0,
	given_per_person_enabled: 0,
}
function SubmitOnChange({
	onChange,
}: {
	onChange: (values: IFlightServicesParams) => void
}) {
	const { values } = useFormState<IFlightServicesParams>({
		subscription: { values: true },
	})
	const { set: setNotifyParentTimeout, clear: clearNotifyParentTimeout } =
		useTimeout()

	const onChangeRef = useRef(onChange)
	onChangeRef.current = onChange

	// notify parent
	useEffect(() => {
		setNotifyParentTimeout(() => {
			onChangeRef.current(values)
		}, 500)
		return () => clearNotifyParentTimeout()
	}, [values, setNotifyParentTimeout, clearNotifyParentTimeout])
	return null
}
const initialBaseSchedule = {
	source: undefined,
	destination: undefined,
	departs_at: undefined,
	arrives_at: undefined,
	airline: undefined,
	fare_type: undefined,
	meal_preference: undefined,
	check_in_baggage: undefined,
	hand_baggage: undefined,
	comments: "",
	remarks: "",
}
/**
 * Flight service
 */

export function FlightService({
	initialValues: initialValuesProp,
	onChange,
	bookingFrom,
	bookingTo,
	no_of_adults = 2,
	children = [],
	tripDestinations,
	flightPreview,
}: IFlightServicesFormProps) {
	const [infants, no_of_children, childrenWithMoreAge] = useMemo(
		() => getChildrenDistribution(children),
		[children]
	)
	no_of_adults = no_of_adults + childrenWithMoreAge
	const currency = useMemo(
		() => tripDestinations.map((d) => d.currency).at(0) || "INR",
		[tripDestinations]
	)
	const pricing = useMemo(() => {
		return {
			...quoteFlightPricingInitialValues,
			currency: currency,
			no_of_adults: no_of_adults,
			children: no_of_children,
			infants: infants,
		}
	}, [infants, no_of_children, currency, no_of_adults])
	const [flightServiceType, setFlightServiceType] = useState<TFlightType>(
		initialValuesProp?.flight_type || "ROUND_TRIP"
	)

	// store the latest "saved" data
	const savedDataRef = useRef<IFlightServicesParams>(initialValuesProp || {})

	// derived transformed values
	const [transformedInitialValues, setTransformedInitialValues] = useState<
		IFlightServicesParams | undefined
	>(
		initialValuesProp
			? {
					flight_preview: flightPreview ?? 1,
					...initialValuesProp,
				}
			: undefined
	)
	const notifyOnChange = useCallback(
		(val: IFlightServicesParams) => {
			savedDataRef.current = val
			const isDataAllFilled = validateFlightData(val.flights)

			if (onChange && isDataAllFilled) {
				onChange(val)
			}
		},
		[onChange]
	)
	const handleFlightSwitch = (flightType: TFlightType) => {
		const flights = savedDataRef.current.flights

		const isDataAllFilled =
			Array.isArray(flights) &&
			flights.some((flight) => {
				return (
					Array.isArray(flight.groups) &&
					flight.groups.some(
						(group) =>
							Array.isArray(group.schedules) &&
							group.schedules.some(
								(schedule) =>
									schedule.source !== undefined ||
									schedule.destination !== undefined ||
									schedule.airline !== undefined ||
									schedule.arrives_at_local !== undefined
							)
					)
				)
			})

		if (
			!isDataAllFilled ||
			window.confirm(
				`Your current flight information will be converted to a ${FLIGHT_TYPE[flightType]} flight type, and you may lose some of your current flight details.`
			)
		) {
			const transformed = transformToFlightTypeInitialValues(
				bookingFrom,
				bookingTo,
				flightType,
				savedDataRef.current,
				pricing
			)
			setTransformedInitialValues(transformed)
			setFlightServiceType(flightType)
		}
	}
	return (
		<Stack gap={"4"}>
			{transformedInitialValues ? null : (
				<Inline gap="2">
					<FlightSearchDialog
						bookingFrom={bookingFrom}
						bookingTo={bookingTo}
						pricing={pricing}
						flightType={flightServiceType}
						onSuccess={(transformedSearchFlightData) => {
							setFlightServiceType(
								transformedSearchFlightData.flight_type || "ROUND_TRIP"
							)
							setTransformedInitialValues(transformedSearchFlightData)
						}}
					>
						{({ onSearch }) => (
							<Button
								level={"primary"}
								onClick={() => {
									onSearch()
								}}
							>
								<Icons.Plus />
								Add flights via API
							</Button>
						)}
					</FlightSearchDialog>

					<Button
						level={"secondary"}
						onClick={() => {
							setTransformedInitialValues(
								transformToFlightTypeInitialValues(
									bookingFrom,
									bookingTo,
									flightServiceType,
									initialValuesProp || {},
									pricing
								)
							)
						}}
					>
						Manual
					</Button>
				</Inline>
			)}
			{transformedInitialValues ? (
				<Stack>
					<Inline gap={"4"} justifyContent={"between"}>
						<Inline gap={"2"} flexWrap={"wrap"}>
							<Button
								level={
									flightServiceType === "ROUND_TRIP" ? "primary" : "secondary"
								}
								onClick={() => handleFlightSwitch("ROUND_TRIP")}
							>
								Round Trip
							</Button>
							<Button
								level={
									flightServiceType === "ONE_WAY" ? "primary" : "secondary"
								}
								onClick={() => handleFlightSwitch("ONE_WAY")}
							>
								One way
							</Button>
							<Button
								level={
									flightServiceType === "MULTI_CITY" ? "primary" : "secondary"
								}
								onClick={() => handleFlightSwitch("MULTI_CITY")}
							>
								Multi City
							</Button>
						</Inline>
						<Button
							type="button"
							level="tertiary"
							onClick={() => {
								if (
									window.confirm(
										`Are you sure you want to remove the flight details? Once you remove this flight, you won’t be able to recover it or undo this action`
									)
								) {
									setTransformedInitialValues(undefined)
									onChange({})
								}
							}}
						>
							<Icons.Cancel /> Remove
						</Button>
					</Inline>
					<Divider sm />
					<FlightServiceForm
						bookingFrom={bookingFrom}
						bookingTo={bookingTo}
						initialValues={transformedInitialValues}
						saveData={(val) => notifyOnChange(val)}
						flightServiceType={flightServiceType}
						pricing={pricing}
					/>
				</Stack>
			) : null}
		</Stack>
	)
}
/**
 * Handles flight data transformations when editing or switching between flight types.
 * Flight data may contain @initialValues during edit operations or during flight type switching.
 * It will not have initial values when creating a new flight service. (choose flight mode first time)
 * Only one flight type (one-way, round-trip, or multi-city) can be active at a time.
 *
 * ### Round-trip
 * - When switching **from one-way**, the existing single group is converted into the onward round-trip group.
 * - When switching **from multi-city**, all existing groups are converted into onward round-trip groups.
 * - Additional information from the first group (in the case of multi-city) is applied to the onward round-trip group.
 *
 * ### One-way
 * - When switching **from round-trip**, the two existing groups (onward and return) remove the return group and onward group is converted into a single group. Additional information from the first group (onward) is retained.
 * - When switching **from multi-city**, all group schedules are merged into a single group containing multiple schedules. Additional information from the first group is retained.
 *
 * ### Multi-city
 * - When switching **from one-way**, the single group with multiple schedules is split into multiple groups — one for each schedule. Additional information is applied to all groups.
 * - When switching **from round-trip**, only onward (each with multiple schedules) are converted into multiple groups, each containing a corresponding schedule and its associated additional information. (Loose the data of the return group)
 */

function transformToFlightTypeInitialValues(
	bookingFrom: string | undefined,
	bookingTo: string | undefined,
	flight_type: TFlightType,
	initialValues: IFlightServicesParams,
	pricing: IFlightPricing
): Required<IFlightServicesParams> {
	const basicConfig = {
		flight_form_fill_mode: initialValues.flight_form_fill_mode || "MANUAL",
		flight_type,
		flight_preview: initialValues.flight_preview ? 1 : (0 as 0 | 1),
	}
	const makeSchedule = (date?: string) => ({
		...initialBaseSchedule,
		departs_at_local: startOf(date || new Date(), "day"),
	})

	const makeFlight = (
		groups: Required<IFlightServicesParams>["flights"][number]["groups"],
		pricing: IFlightPricing
	) => ({
		pricing,
		groups,
	})
	const flight: IFlightServicesParams["flights"] = []
	if (initialValues.flights?.length) {
		initialValues.flights.forEach((f) => {
			const price = f.pricing

			if (flight_type === "ROUND_TRIP") {
				const onwardGroups = (f.groups || []).filter(
					(g) => g.type !== "ROUND_TRIP_RETURN"
				)
				const returnGroups = (f.groups || []).filter(
					(g) => g.type === "ROUND_TRIP_RETURN"
				)
				basicConfig.flight_preview = returnGroups.length
					? basicConfig.flight_preview
					: 0
				flight.push(
					makeFlight(
						[
							{
								...onwardGroups.at(0),
								type: "ROUND_TRIP_ONWARD" as TFlightGroupType,
								schedules: onwardGroups.flatMap((g) => g.schedules),
							},
							{
								...returnGroups.at(0),
								type: "ROUND_TRIP_RETURN" as TFlightGroupType,
								schedules: returnGroups.length
									? returnGroups.flatMap((g) => g.schedules)
									: [makeSchedule(bookingTo)],
							},
						],
						price
					)
				)
			} else if (flight_type === "ONE_WAY") {
				const getExistingSchedules = () =>
					(f.groups || [])
						.filter((g) => g.type !== "ROUND_TRIP_RETURN")
						.flatMap((g) => g.schedules) || [initialBaseSchedule]
				const schedules = getExistingSchedules().map((s) => ({ ...s }))
				flight.push(
					makeFlight(
						[
							{
								...f.groups[0],
								type: "ONE_WAY" as TFlightGroupType,
								schedules,
							},
						],
						price
					)
				)
			} else {
				const multiCityGroups = (f.groups || [])
					.filter((g) => g.type !== "ROUND_TRIP_RETURN")
					.flatMap((g) =>
						g.schedules.map((s) => ({
							...g,
							schedules: [s],
							type: "MULTI_CITY" as TFlightGroupType,
						}))
					)
				flight.push(makeFlight(multiCityGroups, price))
			}
		})
	} else {
		if (flight_type === "ROUND_TRIP") {
			flight.push(
				makeFlight(
					[
						{
							type: "ROUND_TRIP_ONWARD" as TFlightGroupType,
							schedules: [makeSchedule(bookingFrom)],
							info: { category_class: DEFAULT_FLIGHT_CLASS },
						},
						{
							type: "ROUND_TRIP_RETURN" as TFlightGroupType,
							schedules: [makeSchedule(bookingTo)],
							info: { category_class: DEFAULT_FLIGHT_CLASS },
						},
					],
					pricing
				)
			)
		} else if (flight_type === "ONE_WAY") {
			flight.push(
				makeFlight(
					[
						{
							type: "ONE_WAY" as TFlightGroupType,
							schedules: [makeSchedule(bookingFrom)],
							info: { category_class: DEFAULT_FLIGHT_CLASS },
						},
					],
					pricing
				)
			)
		} else {
			flight.push(
				makeFlight(
					[
						{
							type: "MULTI_CITY" as TFlightGroupType,
							schedules: [makeSchedule(bookingFrom)],
							info: { category_class: DEFAULT_FLIGHT_CLASS },
						},
					],
					pricing
				)
			)
		}
	}

	return {
		...basicConfig,
		flights: flight,
	}
}

/**
 *
 * Auto flight service
 */

const FlightSearchSchema = Validator.object().shape({
	onward_flight_details: Validator.array()
		.of(
			Validator.object().shape({
				departure_date: Validator.date().required("Departure Date is required"),
				flight_number: Validator.object()
					.required("Flight Number is required")
					.nullable(),
				departure_airport: Validator.object()
					.required("Departure Airport is required")
					.nullable(),
			})
		)
		.required(),
	return_flight_details: Validator.array().when("flight_type", {
		is: (t: string) => t === "ROUND_TRIP",
		then: (schema) =>
			schema
				.of(
					Validator.object().shape({
						departure_date: Validator.date().required(
							"Departure Date is required"
						),
						flight_number: Validator.object()
							.required("Flight Number is required")
							.nullable(),
						departure_airport: Validator.object()
							.required("Departure Airport is required")
							.nullable(),
					})
				)
				.required(),
	}),
})

const searchValidation = validateFormValues(FlightSearchSchema)

export interface IPreviewFlightData {
	from: string
	to: string
	type_of_flight: string
	flight_number: string[]
	via: Array<string>
	no_flight: string
	remarks?: string
	info?: {
		adult_check_in_baggage?: string
		adult_hand_baggage?: string
		infant_check_in_baggage?: string
		infant_hand_baggage?: string
		fare_type?: string
		meal_preference?: string
		category_class?: TFlightClass
	}
	flight_details: {
		departure?: IFlightAirPort
		arrival?: IFlightAirPort
		duration?: string
		duration_formatted?: string
		flight: {
			id?: number | string
			number_plate: string
			airline?: TWorldAirline
		}
	}[]
}

function FlightSearchField({
	flightsLength,
	index,
	name,
	add,
	remove,
	bookingFrom,
}: {
	name: string
	index: number
	flightsLength: number
	add: () => void
	remove: () => void
	bookingFrom?: string
}) {
	// It’s very rare for any connecting flight to have more than four connections, so we currently handle up to four connecting flights.
	const MAX_CONNECTING_FLIGHTS = 6
	return (
		<Inline gap={"2"} alignItems={"start"} width={"full"} flexWrap={"wrap"}>
			<Box style={{ width: "150px" }}>
				<DatePickerField
					label="Departure Date"
					name={`${name}.departure_date`}
					dateFormat="DD MMM 'YY"
				/>
			</Box>

			<Box style={{ maxWidth: "150px" }}>
				<SelectField
					select={SelectAirlines}
					label="Flight Number"
					name={`${name}.flight_number`}
					creatable={false}
				/>
			</Box>

			<SelectField
				select={SelectAirports}
				label="Departure Airport"
				name={`${name}.departure_airport`}
				creatable={false}
			/>

			<Inline paddingTop={"6"} gap={"2"}>
				{flightsLength < MAX_CONNECTING_FLIGHTS ? (
					<Button status="primary" onClick={add}>
						+
					</Button>
				) : null}
				{flightsLength > 1 ? (
					<Button onClick={remove}>
						<Icons.Cancel />
					</Button>
				) : null}
			</Inline>
		</Inline>
	)
}

function FlightSearchForm({
	bookingFrom,
	bookingTo,
	searchInitialValue,
	onSuccess,
	hideFlightType = false,
}: {
	searchInitialValue: IFlightSearchParams
	onChange?: (data: IFlightServicesParams) => void
	bookingFrom?: string
	bookingTo?: string
	onSuccess: (
		flightData: Array<IFlightSchedules>,
		flightSearchData: IFlightSearchParams
	) => void
	hideFlightType?: boolean
}) {
	const xhr = useXHR()
	return (
		<Form<IFlightSearchParams>
			initialValues={searchInitialValue}
			validate={searchValidation}
			onSubmit={withServerErrors(async (val) => {
				const allFlights = [
					...(val.onward_flight_details || []),
					...(val.return_flight_details || []),
				]

				const items = allFlights.map((flight) => ({
					date: dateToUTCString(
						endOf(flight.departure_date, "day"),
						"YYYY-MM-DD"
					),
					flight: flight.flight_number?.number_plate,
					airport_id: flight.departure_airport?.id,
				}))

				const data: Array<IFlightSchedules> = await xhr
					.get(`/flight-schedule`, { params: { flights: items } })
					.then((resp) => resp.data.data)
				onSuccess(data, val)
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ handleSubmit, form, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<GetFieldValue<IFlightSearchParams["flight_type"]> name="flight_type">
						{({ value: flightType }) => (
							<Stack gap={"4"}>
								{hideFlightType ? null : (
									<>
										<Inline
											gap={"2"}
											justifyContent={"between"}
											paddingBottom={"2"}
											borderBottomWidth={"1"}
										>
											<GetFieldValue<
												IFlightSearchParams["onward_flight_details"]
											> name="onward_flight_details">
												{({ value: onWardFlightDetails }) => (
													<Inline gap={"2"} flexWrap={"wrap"}>
														<Button
															onClick={() =>
																form.batch(() => {
																	form.change("flight_type", "ROUND_TRIP")
																	form.change("onward_flight_details", [
																		...onWardFlightDetails,
																	])
																	form.change("return_flight_details", [
																		{
																			departure_date: startOf(
																				bookingTo || new Date(),
																				"day"
																			),
																			flight_number: undefined,
																		},
																	])
																})
															}
															level={
																flightType === "ROUND_TRIP"
																	? "primary"
																	: "secondary"
															}
															disabled={submitting}
														>
															Round Trip
														</Button>
														<Button
															onClick={() =>
																form.batch(() => {
																	form.change("flight_type", "ONE_WAY")
																	form.change(
																		"return_flight_details",
																		undefined
																	)
																	form.change("onward_flight_details", [
																		...onWardFlightDetails,
																	])
																})
															}
															level={
																flightType === "ONE_WAY"
																	? "primary"
																	: "secondary"
															}
															disabled={submitting}
														>
															One Way
														</Button>
														<Button
															onClick={() =>
																form.batch(() => {
																	form.change("flight_type", "MULTI_CITY")
																	form.change("onward_flight_details", [
																		...onWardFlightDetails,
																	])

																	form.change(
																		"return_flight_details",
																		undefined
																	)
																})
															}
															level={
																flightType === "MULTI_CITY"
																	? "primary"
																	: "secondary"
															}
															disabled={submitting}
														>
															Multi City
														</Button>
													</Inline>
												)}
											</GetFieldValue>
										</Inline>
									</>
								)}

								<Stack gap={"4"}>
									<Stack>
										{flightType === "ROUND_TRIP" ? (
											<Stack
												alignItems={"center"}
												gap={"4"}
												flexWrap={"wrap"}
												paddingTop={"2"}
											>
												<BoxBadge
													title="Onward"
													icon={<Icons.CircleArrow rotate="180" />}
												>
													<Box>
														<FieldArray<
															IFlightSearchParams["onward_flight_details"][number]
														> name="onward_flight_details">
															{({ fields }) => (
																<Stack gap="4">
																	{fields.map((name, index) => (
																		<FlightSearchField
																			key={name}
																			index={index}
																			name={name}
																			flightsLength={fields.length || 0}
																			add={() =>
																				fields.push({
																					departure_date: startOf(
																						bookingFrom || new Date(),
																						"day"
																					),
																					flight_number: undefined,
																				})
																			}
																			remove={() => fields.remove(index)}
																			bookingFrom={bookingFrom}
																		/>
																	))}
																</Stack>
															)}
														</FieldArray>
													</Box>
												</BoxBadge>
												<Box
													display="inlineFlex"
													size="8"
													alignItems="center"
													justifyContent="center"
													borderWidth={"1"}
													borderColor={"muted"}
													rounded="full"
													bgColor={"primary"}
												>
													<Icons.SwitchHorizontal rotate="90" />
												</Box>

												<BoxBadge title="Return" icon={<Icons.CircleArrow />}>
													<Box>
														<FieldArray<
															Required<IFlightSearchParams>["return_flight_details"][number]
														> name="return_flight_details">
															{({ fields }) => (
																<Stack gap="4">
																	{fields.map((name, index) => (
																		<FlightSearchField
																			key={name}
																			index={index}
																			name={name}
																			flightsLength={fields.length || 0}
																			add={() =>
																				fields.push({
																					departure_date: startOf(
																						bookingTo || new Date(),
																						"day"
																					),
																					flight_number: undefined,
																				})
																			}
																			remove={() => fields.remove(index)}
																			bookingFrom={bookingFrom}
																		/>
																	))}
																</Stack>
															)}
														</FieldArray>
													</Box>
												</BoxBadge>
											</Stack>
										) : (
											<FieldArray<
												IFlightSearchParams["onward_flight_details"][number]
											> name="onward_flight_details">
												{({ fields }) => (
													<Stack gap="4">
														{fields.map((name, index) => (
															<FlightSearchField
																key={index}
																index={index}
																name={name}
																flightsLength={fields.length || 0}
																add={() =>
																	fields.push({
																		departure_date: startOf(
																			bookingFrom || new Date(),
																			"day"
																		),
																		flight_number: undefined,
																	})
																}
																remove={() => fields.remove(index)}
																bookingFrom={bookingFrom}
															/>
														))}
													</Stack>
												)}
											</FieldArray>
										)}
									</Stack>
									{submitting ? (
										<Inline justifyContent={"center"} alignItems={"center"}>
											<Spinner />
										</Inline>
									) : null}
									<SubmissionError />

									<Box borderTopWidth={"1"} paddingTop={"2"}>
										<Button
											type="submit"
											level={submitting ? "secondary" : "primary"}
											disabled={submitting}
										>
											<Icons.Search />
											{submitting ? "Searching..." : "Search"}
										</Button>
									</Box>
								</Stack>
							</Stack>
						)}
					</GetFieldValue>
				</form>
			)}
		</Form>
	)
}

function transformApiFlightDataToForm(
	basicDetails: IFlightSearchParams,
	flights: Array<IFlightSchedules>,
	pricing: IFlightPricing
): { flightDetails: IFlightServicesParams; errorFlightNo: Array<string> } {
	let flightPreview: IFlightServicesParams["flight_preview"] = 1
	const errorFlightNo: string[] = []

	// ---------- Convert a single flight to schedule ----------
	const toSchedule = (flight: IFlightSchedules) => {
		const s = flight.schedule
		if (!s) {
			const searchDetails = [
				...basicDetails.onward_flight_details,
				...(basicDetails.return_flight_details || []),
			].find(
				(f) =>
					f.flight_number?.number_plate_formatted ===
					flight.flight.number_plate_formatted
			)
			errorFlightNo.push(flight.flight.number_plate_formatted)
			flightPreview = 0
			return {
				...initialBaseSchedule,
				airline: flight.flight,
				source: searchDetails?.departure_airport,
				departs_at_local: searchDetails?.departure_date,
			}
		}

		return {
			source: s.departure.airport
				? addLabelToAirport(s.departure.airport)
				: undefined,
			destination: s.arrival.airport
				? addLabelToAirport(s.arrival.airport)
				: undefined,
			departs_at: s.departure.schedule_date,
			arrives_at: s.arrival.schedule_date,
			departs_at_local: parseDate(
				s.departure.schedule_date_local,
				"YYYY-MM-DD HH:mm:ss"
			),
			arrives_at_local: parseDate(
				s.arrival.schedule_date_local,
				"YYYY-MM-DD HH:mm:ss"
			),
			departure_terminal: s.departure.terminal || undefined,
			arrival_terminal: s.arrival.terminal || undefined,
			airline: flight.flight,
			remarks: "",
		}
	}

	// ---------- Build schedule group ----------
	const buildSchedules = (
		flightDetails: typeof basicDetails.onward_flight_details | undefined
	): NonNullable<
		IFlightServicesParams["flights"]
	>[number]["groups"][number]["schedules"] => {
		if (!flightDetails) return []
		return flightDetails.map((data) => {
			const match = flights.find(
				(f) =>
					f.flight.number_plate_formatted ===
						data.flight_number?.number_plate_formatted &&
					data.departure_airport?.id === f.schedule?.departure.airport?.id
			)
			if (!match?.schedule) {
				flightPreview = 0
				errorFlightNo.push(
					match?.flight.number_plate_formatted ||
						data.flight_number?.number_plate_formatted ||
						""
				)
				return {
					...initialBaseSchedule,
					airline: data.flight_number,
					source: data.departure_airport,
					departs_at_local: data.departure_date,
				}
			}
			return toSchedule(match)
		})
	}

	// ---------- ONE WAY ----------
	if (basicDetails.flight_type === "ONE_WAY") {
		const schedules = flights.map(toSchedule)
		return {
			flightDetails: {
				flight_form_fill_mode: "AUTO",
				flight_type: basicDetails.flight_type,
				flight_preview: flightPreview,
				flights: [
					{
						pricing,
						groups: [
							{
								type: "ONE_WAY",
								schedules,
								info: { category_class: DEFAULT_FLIGHT_CLASS },
							},
						],
					},
				],
			},
			errorFlightNo,
		}
	}

	// ---------- ROUND TRIP ----------
	if (basicDetails.flight_type === "ROUND_TRIP") {
		const onwardSchedules = buildSchedules(basicDetails.onward_flight_details)
		const returnSchedules = buildSchedules(basicDetails.return_flight_details)

		return {
			flightDetails: {
				flight_form_fill_mode: "AUTO",
				flight_type: basicDetails.flight_type,
				flight_preview: flightPreview,
				flights: [
					{
						pricing,
						groups: [
							{
								type: "ROUND_TRIP_ONWARD",
								schedules: onwardSchedules,
								info: { category_class: DEFAULT_FLIGHT_CLASS },
							},
							{
								type: "ROUND_TRIP_RETURN",
								schedules: returnSchedules,
								info: { category_class: DEFAULT_FLIGHT_CLASS },
							},
						],
					},
				],
			},
			errorFlightNo,
		}
	}

	// ---------- MULTI CITY ----------
	const groups = flights.map((f) => ({
		type: "MULTI_CITY" as TFlightGroupType,
		schedules: [toSchedule(f)],
		info: { category_class: DEFAULT_FLIGHT_CLASS },
	}))

	return {
		flightDetails: {
			flight_form_fill_mode: "AUTO",
			flight_type: basicDetails.flight_type,
			flight_preview: flightPreview,
			flights: [
				{
					pricing,
					groups,
				},
			],
		},
		errorFlightNo,
	}
}

function FlightSearchDialog({
	bookingFrom,
	bookingTo,
	flightType,
	pricing,
	onSuccess,
	children,
	hideFlightType = false,
}: {
	bookingFrom?: string
	bookingTo?: string
	flightType: TFlightType
	pricing: IFlightPricing
	onSuccess: (values: IFlightServicesParams, showWarning: boolean) => void
	children: (props: {
		onSearch: (apiSearchFormValues?: IFlightSearchParams) => void
	}) => React.ReactNode
	hideFlightType?: boolean
}) {
	const [isSearchDialogOpen, openSearchDialog, closeSearchDialog] = useDialog()
	const [isErrorDialogOpen, openErrorDialog, closeErrorDialog] = useDialog()
	const [searchFormValues, setSearchFormValues] = useState<IFlightSearchParams>(
		{
			flight_type: flightType,
			return_flight_details:
				flightType === "ROUND_TRIP" || flightType === undefined
					? [
							{
								departure_date: startOf(bookingTo || new Date(), "day"),
								flight_number: undefined,
							},
						]
					: undefined,
			onward_flight_details: [
				{
					departure_date: startOf(bookingFrom || new Date(), "day"),
					flight_number: undefined,
				},
			],
		}
	)
	const [transformedSearchFlightData, setTransformedSearchFlightData] =
		useState<{
			flightDetails: IFlightServicesParams
			errorFlightNo: Array<string>
		}>({ flightDetails: {}, errorFlightNo: [] })

	return (
		<>
			{children({
				onSearch: (values) => {
					if (values) {
						setSearchFormValues(values)
					}
					openSearchDialog()
				},
			})}
			<Dialog
				open={isSearchDialogOpen}
				onClose={closeSearchDialog}
				title="Add flights via API"
			>
				<Dialog.Body>
					<FlightSearchForm
						bookingFrom={bookingFrom}
						bookingTo={bookingTo}
						searchInitialValue={searchFormValues}
						onSuccess={(FlightData, SearchFormValues) => {
							const transformedData = transformApiFlightDataToForm(
								SearchFormValues,
								FlightData,
								pricing
							)
							setTransformedSearchFlightData(transformedData)
							if (transformedData.errorFlightNo.length > 0) {
								setSearchFormValues(SearchFormValues)
								closeSearchDialog()
								openErrorDialog()
							} else {
								onSuccess(transformedData.flightDetails, true)
								closeSearchDialog()
							}
						}}
						hideFlightType={hideFlightType}
					/>
				</Dialog.Body>
			</Dialog>
			<Dialog
				open={isErrorDialogOpen}
				onClose={closeErrorDialog}
				title="Missing Flight Details"
				sm
			>
				<Dialog.Body>
					<Text>
						Sorry, we are unable to fetch flight details of{" "}
						<strong>
							{transformedSearchFlightData.errorFlightNo
								.map((flightNo) => flightNo)
								.join(", ")}
						</strong>
						. Please continue by adding details of missing flights manually or
						search again.
					</Text>
				</Dialog.Body>
				<Dialog.Footer>
					<Button
						onClick={() => {
							onSuccess(transformedSearchFlightData.flightDetails, false)
							closeErrorDialog()
						}}
					>
						Add Manually
					</Button>
					<Button
						level="primary"
						onClick={() => {
							closeErrorDialog()
							openSearchDialog()
						}}
					>
						Search Again
					</Button>
				</Dialog.Footer>
			</Dialog>
		</>
	)
}
/**
 *
 * common form components
 */

// if the required data is filled then it will return true and then user will able to see the preview
const validateFlightData = (
	flights: IFlightServicesParams["flights"]
): boolean => {
	return (
		Array.isArray(flights) &&
		flights.every((flight) => {
			const pricing = flight.pricing
			if (pricing.no_of_adults === undefined || pricing.no_of_adults === 0) {
				return false
			}

			return (
				Array.isArray(flight.groups) &&
				flight.groups.every(
					(group) =>
						Array.isArray(group.schedules) &&
						group.schedules.every(
							(schedule) =>
								schedule.source !== undefined &&
								schedule.destination !== undefined &&
								schedule.airline !== undefined &&
								schedule.arrives_at_local !== undefined &&
								schedule.departs_at_local !== undefined
						)
				)
			)
		})
	)
}

function FlightServiceForm({
	bookingFrom,
	bookingTo,
	pricing,
	initialValues: initialValuesProp,
	saveData,
	flightServiceType,
}: {
	bookingFrom?: string
	bookingTo?: string
	pricing: IFlightPricing
	initialValues: IFlightServicesParams
	saveData?: (val: IFlightServicesParams) => void
	flightServiceType: TFlightType
}) {
	const sectionRef = useRef<HTMLDivElement>(null)
	const flightType = useMemo(() => flightServiceType, [flightServiceType])
	const notifyOnChange = useCallback(
		(flattenValues: IFlightServicesParams) => {
			saveData?.(flattenValues)
		},
		[saveData]
	)
	return (
		<Box ref={sectionRef}>
			<Form<IFlightServicesParams>
				key={flightType}
				initialValues={initialValuesProp}
				validate={validate}
				onSubmit={() => {
					// managed by <SubmitOnChange />.
					// react-final-form requires this prop.
				}}
				subscription={{}}
				mutators={{ ...arrayMutators }}
			>
				{({ handleSubmit, form }) => (
					<form onSubmit={handleSubmit} noValidate>
						<Stack gap={"4"}>
							<FieldArray<
								Required<IFlightServicesParams>["flights"][number]
							> name="flights">
								{({ fields: flights }) =>
									flights.map((flightName, flightIndex) => (
										<Stack key={flightIndex} gap={"2"}>
											<Grid>
												<Col
													md={12}
													lg={8}
													paddingBottom={{ xs: "4", md: "0" }}
													paddingX={"0"}
												>
													<GetFieldValue<0 | 1> name="flight_preview">
														{({ value: isPreview }) => (
															<>
																{!isPreview ? (
																	<Box>
																		<GroupInputFields
																			flightName={flightName}
																			flightType={flightType}
																			bookingFrom={bookingFrom}
																			bookingTo={bookingTo}
																			pricing={pricing}
																		/>
																	</Box>
																) : (
																	<Box paddingY={"4"} paddingRight={"4"}>
																		<FormFlightPreview name={flightName} />
																	</Box>
																)}
																<Inline
																	justifyContent={"end"}
																	paddingRight={"4"}
																	gap={"2"}
																	paddingTop={"4"}
																>
																	{(flights.length || 0) > 1 ? (
																		<Button
																			type="button"
																			level="tertiary"
																			onClick={() =>
																				flights.remove(flightIndex)
																			}
																		>
																			<Icons.Cancel /> Remove
																		</Button>
																	) : null}

																	{!isPreview ? (
																		// validate the files are filled if not then show the error focus on the fields
																		(flights.length || 0) ===
																		flightIndex + 1 ? (
																			<Button
																				type="submit"
																				status="primary"
																				onClick={() => {
																					const flightData =
																						form.getFieldState("flights")?.value
																					if (
																						flightData &&
																						validateFlightData(flightData)
																					) {
																						form.change("flight_preview", 1)
																						sectionRef.current?.scrollIntoView({
																							behavior: "smooth",
																						})
																					}
																				}}
																			>
																				Done
																			</Button>
																		) : null
																	) : (
																		<Button
																			type="button"
																			status="primary"
																			onClick={() => {
																				form.change("flight_preview", 0)
																			}}
																		>
																			Edit
																		</Button>
																	)}
																</Inline>
															</>
														)}
													</GetFieldValue>
												</Col>
												<Col paddingX={"0"} position="relative">
													<PricingInputFields
														name={flightName}
														maxPax={{
															adults: pricing.no_of_adults,
															children: pricing.children,
															infants: pricing.infants,
														}}
													/>
												</Col>
											</Grid>
											{(flights.length || 0) === flightIndex + 1 ? (
												<Box>
													<Button
														type="button"
														status="primary"
														onClick={() => {
															const flightData =
																transformToFlightTypeInitialValues(
																	bookingFrom,
																	bookingTo,
																	flightServiceType,
																	{},
																	pricing
																).flights[0]

															flights.push({
																...flightData,
															})
															form.change("flight_preview", 0)
														}}
													>
														<Icons.Plus /> Add More Flight
													</Button>
												</Box>
											) : (
												<Box borderTopWidth={"2"} marginY={"4"} />
											)}
										</Stack>
									))
								}
							</FieldArray>
						</Stack>
						<SubmitOnChange onChange={notifyOnChange} />
					</form>
				)}
			</Form>
		</Box>
	)
}
function GroupInputFields({
	flightName,
	flightType,
	bookingFrom,
	bookingTo,
	pricing,
}: {
	flightName: string
	flightType: TFlightType
	bookingFrom?: string
	bookingTo?: string
	pricing: IFlightPricing
}) {
	const flightRef = useRef<HTMLDivElement>(null)
	const [showWarning, setShowWarning] = useState(false)
	const form = useForm()

	return (
		<Box>
			<Stack gap={"2"} paddingRight={"4"}>
				<Inline justifyContent={showWarning ? "between" : "end"}>
					{showWarning ? (
						<Alert inline status="warning" type="polite">
							Please re-check the flight details before proceeding.
						</Alert>
					) : null}
					<FlightSearchDialog
						bookingFrom={bookingFrom}
						bookingTo={bookingTo}
						flightType={flightType}
						pricing={pricing}
						onSuccess={(transformedSearchFlightData, showWarning) => {
							setShowWarning(showWarning)
							// only one flight complete can be search at a time so we can set the first flight
							form.change(
								`${flightName}`,
								transformedSearchFlightData.flights?.[0]
							)
						}}
						hideFlightType
					>
						{({ onSearch }) => (
							<Button
								status="primary"
								onClick={() => {
									const flightData:
										| Required<IFlightServicesParams>["flights"][number]
										| undefined = getFormValueForKey(
										form.getState().values,
										flightName
									)

									if (!flightData) {
										onSearch()
										return
									}

									const groups = flightData.groups ?? []

									const mapSchedule = (
										s: Required<IFlightServicesParams>["flights"][number]["groups"][number]["schedules"][number]
									) => ({
										departure_date: startOf(
											s.departs_at_local || bookingTo || new Date(),
											"day"
										),
										flight_number: s.airline,
										departure_airport: s.source
											? addLabelToAirport(s.source)
											: undefined,
									})

									const onwardGroups = groups.filter(
										(g) => g.type !== "ROUND_TRIP_RETURN"
									)
									const returnGroups = groups.filter(
										(g) => g.type === "ROUND_TRIP_RETURN"
									)

									const onwardDetails =
										onwardGroups.length > 0
											? onwardGroups.flatMap((g) =>
													g.schedules.map(mapSchedule)
												)
											: [
													{
														departure_date: startOf(
															bookingFrom || new Date(),
															"day"
														),
														flight_number: undefined,
													},
												]

									const returnDetails =
										flightType === "ROUND_TRIP"
											? returnGroups.flatMap((g) =>
													g.schedules.map(mapSchedule)
												)
											: undefined

									const apiData = {
										flight_type: flightType,
										onward_flight_details: onwardDetails,
										return_flight_details: returnDetails,
									}

									onSearch(apiData)
								}}
							>
								<Icons.Refresh />
								Refresh via api
							</Button>
						)}
					</FlightSearchDialog>
				</Inline>

				<FieldArray<
					Required<IFlightServicesParams>["flights"][number]["groups"][number]
				>
					name={`${flightName}.groups`}
				>
					{({ fields: groups }) =>
						groups.map((groupName, groupIndex) => (
							<Stack
								key={groupIndex}
								alignItems={"center"}
								width={"full"}
								gap={"2"}
							>
								<Stack
									position={"relative"}
									paddingX={"2"}
									paddingY={"4"}
									borderRadius="md"
									borderWidth={"1"}
									borderColor={"muted"}
									width={"full"}
									gap={"4"}
								>
									{flightType === "ROUND_TRIP" ? (
										<GetFieldValue<
											Required<IFlightServicesParams>["flights"][number]["groups"][number]["type"]
										>
											name={`${groupName}.type`}
										>
											{({ value: flightGroupType }) => {
												// flight group type will be always ROUND_TRIP_ONWARD or ROUND_TRIP_RETURN bcz we are checking for only ROUND_TRIP
												const typeOfRoundTrip =
													flightGroupType === "ROUND_TRIP_ONWARD"
														? "Onward"
														: "Return"
												return (
													<BoxBadge
														title={typeOfRoundTrip}
														icon={
															typeOfRoundTrip === "Onward" ? (
																<Icons.CircleArrow rotate="180" />
															) : (
																<Icons.CircleArrow />
															)
														}
													>
														<FieldArray<
															Required<IFlightServicesParams>["flights"][number]["groups"][number]["schedules"][number]
														>
															name={`${groupName}.schedules`}
														>
															{({ fields: schedules }) =>
																schedules.map((scheduleName, scheduleIndex) => (
																	<Stack
																		width={"full"}
																		key={scheduleIndex}
																		ref={flightRef}
																	>
																		<ScheduleInputFields
																			name={scheduleName}
																			groupName={groupName}
																			index={scheduleIndex}
																			bookingFrom={bookingFrom}
																			onRemove={(val) => schedules.remove(val)}
																			onAddingSchedule={(val) => {
																				schedules.push(val)
																				flightRef.current?.scrollIntoView({
																					behavior: "smooth",
																				})
																			}}
																			schedulesLength={schedules.length}
																		/>
																	</Stack>
																))
															}
														</FieldArray>
													</BoxBadge>
												)
											}}
										</GetFieldValue>
									) : (
										<FieldArray<
											Required<IFlightServicesParams>["flights"][number]["groups"][number]["schedules"][number]
										>
											name={`${groupName}.schedules`}
										>
											{({ fields: schedules }) =>
												schedules.map((scheduleName, scheduleIndex) => (
													<Stack
														width={"full"}
														key={scheduleIndex}
														ref={flightRef}
													>
														{flightType === "MULTI_CITY" ? (
															<ScheduleInputFields
																name={scheduleName}
																groupName={groupName}
																bookingFrom={bookingFrom}
																index={groupIndex}
																onRemove={(val) => groups.remove(val)}
																onAddingGroup={(val) => {
																	groups.push(val)
																	flightRef.current?.scrollIntoView({
																		behavior: "smooth",
																	})
																}}
																schedulesLength={groups.length}
															/>
														) : (
															<ScheduleInputFields
																name={scheduleName}
																bookingFrom={bookingFrom}
																groupName={groupName}
																index={scheduleIndex}
																onRemove={(val) => schedules.remove(val)}
																onAddingSchedule={(val) => {
																	schedules.push(val)
																	flightRef.current?.scrollIntoView({
																		behavior: "smooth",
																	})
																}}
																schedulesLength={schedules.length}
															/>
														)}
													</Stack>
												))
											}
										</FieldArray>
									)}
									<Grid gap={"2"}>
										<Col paddingX={"0"} md={4}>
											<SelectField
												label="Additional Options"
												secondaryLabel="optional"
												name={`${groupName}.additional_options`}
												select={Select}
												placeholder=" Baggage / Fare / Meal"
												multiple
												creatable={false}
												options={ADDITIONAL_FLIGHT_OPTIONS}
											/>
										</Col>
										<Col
											borderColor={"muted"}
											borderWidth={"1"}
											borderRadius={"md"}
										>
											<Box padding={"4"}>
												<AdditionalScheduleInputField
													name={`${groupName}`}
													flightName={`${flightName}`}
												/>
											</Box>
										</Col>
									</Grid>
								</Stack>
								{groupIndex < (groups.length || 1) - 1 ? (
									<>
										<Box
											display={"flex"}
											alignItems={"center"}
											justifyContent={"center"}
											rounded="full"
											size="12"
											borderWidth={"1"}
											borderColor={"muted"}
											backgroundColor={"primary"}
										>
											{flightType === "ROUND_TRIP" ? (
												<Icons.SwitchHorizontal rotate="90" />
											) : (
												<Icons.ArrowLeft rotate="270" />
											)}
										</Box>
									</>
								) : null}
							</Stack>
						))
					}
				</FieldArray>
			</Stack>
		</Box>
	)
}

function SyncPerPersonPricing({
	name,
	adults,
	children,
	infants,
	perPersonCostPrice,
	perPersonGivenPrice,
	isPerPersonCostPrice,
	isPerPersonGivenPrice,
	maxPax,
	onPaxAdjusted,
}: {
	name: string
	adults: number
	children: number
	infants: number
	perPersonCostPrice: IFlightPricing["per_person_cost_price"]
	perPersonGivenPrice: IFlightPricing["per_person_given_price"]
	isPerPersonCostPrice: boolean
	isPerPersonGivenPrice: boolean
	maxPax: { adults: number; children: number; infants: number }
	onPaxAdjusted?: (adjustedTypes: TFlightPaxType[]) => void
}) {
	const form = useForm()
	const { value: flightCurrency } = useFieldValue<string | undefined>(
		`${name}.currency`
	)
	const pax = useMemo(
		() => ({ adults, children, infants }),
		[adults, children, infants]
	)

	// Update pax count to the new maximum limit , if user decreases the pax count on quote level
	// When user increases the pax count on quote level - there will be no update in form
	// as user can increase the pax count to the new max limit.
	useEffect(() => {
		const adjustedTypes: TFlightPaxType[] = []
		form.batch(() => {
			if (adults > maxPax.adults) {
				form.change(`${name}.no_of_adults`, maxPax.adults)
				adjustedTypes.push("adult")
			}
			if (children > maxPax.children) {
				form.change(`${name}.children`, maxPax.children)
				adjustedTypes.push("child")
			}
			if (infants > maxPax.infants) {
				form.change(`${name}.infants`, maxPax.infants)
				adjustedTypes.push("infant")
			}
		})
		if (adjustedTypes.length) {
			onPaxAdjusted?.(adjustedTypes)
		}
	}, [
		maxPax.adults,
		maxPax.children,
		maxPax.infants,
		adults,
		children,
		infants,
		form,
		name,
		onPaxAdjusted,
	])

	// Clear stale per-person values when a pax type has a zero count,
	// so removed passengers don't retain unused pricing.
	useEffect(() => {
		const paxTypes = [
			{ type: "adult", count: adults },
			{ type: "child", count: children },
			{ type: "infant", count: infants },
		] as const

		form.batch(() => {
			paxTypes.forEach(({ type, count }) => {
				if (count === 0) {
					form.change(`${name}.per_person_cost_price.${type}`, undefined)
					form.change(`${name}.per_person_given_price.${type}`, undefined)
				}
			})
		})
	}, [adults, children, infants, form, name])

	// Keep the total Cost Price in sync with the per-person Cost Price values.
	// Whenever per-person Cost Price or passenger counts change, recalculate the total.
	useEffect(() => {
		if (!isPerPersonCostPrice) return
		form.change(
			`${name}.calculated_price`,
			getFlightPerPersonTotal(perPersonCostPrice, pax, flightCurrency || "INR")
		)
	}, [
		form,
		name,
		isPerPersonCostPrice,
		perPersonCostPrice,
		adults,
		children,
		infants,
		pax,
		flightCurrency,
	])

	// Keep the total Given Price in sync with the per-person Given Price values.
	// Whenever per-person Given Price or passenger counts change, recalculate the total.
	useEffect(() => {
		if (!isPerPersonGivenPrice) return
		form.change(
			`${name}.given_price`,
			getFlightPerPersonTotal(perPersonGivenPrice, pax, flightCurrency || "INR")
		)
	}, [
		form,
		name,
		isPerPersonGivenPrice,
		perPersonGivenPrice,
		adults,
		children,
		infants,
		pax,
		flightCurrency,
	])

	return null
}

// hook to get total cost / given amounts for pax(A/C/I)
function usePaxTotals(
	perPersonCostPrice?: IFlightPricing["per_person_cost_price"],
	perPersonGivenPrice?: IFlightPricing["per_person_given_price"],
	adults = 0,
	children = 0,
	infants = 0,
	currency = "INR"
) {
	return useMemo(() => {
		const total = (value: number | undefined, count: number) =>
			Number(
				formatMoneyByDecimal(
					multipleMoneyBy(
						moneyParseByDecimal(Number(value ?? 0), currency),
						count
					)
				)
			)
		return {
			cost_price: {
				adult: total(perPersonCostPrice?.adult, adults),
				child: total(perPersonCostPrice?.child, children),
				infant: total(perPersonCostPrice?.infant, infants),
			},
			given_price: {
				adult: total(perPersonGivenPrice?.adult, adults),
				child: total(perPersonGivenPrice?.child, children),
				infant: total(perPersonGivenPrice?.infant, infants),
			},
		}
	}, [
		perPersonCostPrice,
		perPersonGivenPrice,
		adults,
		children,
		infants,
		currency,
	])
}

function formatPaxTypesList(types: string[]) {
	if (types.length === 1) return types[0]
	if (types.length === 2) return `${types[0]} and ${types[1]}`
	return `${types.slice(0, -1).join(", ")}, and ${types[types.length - 1]}`
}

// Pricing form Table
function PricingInputFields({
	name,
	disableEditing = false,
	maxPax,
}: {
	name: string
	disableEditing?: boolean
	maxPax: { adults: number; children: number; infants: number }
}) {
	const form = useForm()
	const flightPricingName = `${name}.pricing`

	const { value: groups = [] } = useFieldValue<TGroup[]>(`${name}.groups`)
	const { value: pricing } = useFieldValue<IFlightPricing>(flightPricingName)

	const {
		no_of_adults: adults,
		children,
		infants,
		comments,
		calculated_price: calculatedPrice = 0,
		given_price: givenPrice = 0,
		per_person_cost_price: perPersonCostPrice,
		per_person_given_price: perPersonGivenPrice,
		currency,
		cost_per_person_enabled,
		given_per_person_enabled,
	} = pricing

	const [commentsVisible, showAddComments] = useState<boolean>(
		Boolean(comments)
	)
	const isPerPersonCostPrice = Boolean(cost_per_person_enabled)
	const isPerPersonGivenPrice = Boolean(given_per_person_enabled)
	const [adjustedPaxTypes, setAdjustedPaxTypes] = useState<TFlightPaxType[]>([])

	const totalPax = adults + children + infants
	const totals = usePaxTotals(
		perPersonCostPrice,
		perPersonGivenPrice,
		adults,
		children,
		infants,
		currency
	)
	const flightNumbers = getFlightNumbers(groups)
	const allCurrencies = useCurrenciesCodesOfTenant()

	// To distribute total price equally or based on existing per-person weights.
	const distributePrice = useCallback(
		({
			field,
			totalPrice,
		}: {
			field: "per_person_cost_price" | "per_person_given_price"
			totalPrice: number
		}) => {
			if (!totalPax) {
				form.change(`${flightPricingName}.${field}`, undefined)
				return
			}

			// Source pricing for weightage calculation
			const sourcePricing =
				field === "per_person_cost_price"
					? perPersonGivenPrice
					: perPersonCostPrice

			form.change(
				`${flightPricingName}.${field}`,
				computePerPersonDistribution(
					totalPrice,
					{ adults, children, infants },
					{ sourceWeights: sourcePricing, currency }
				)
			)
		},
		[
			form,
			flightPricingName,
			adults,
			children,
			infants,
			totalPax,
			perPersonCostPrice,
			perPersonGivenPrice,
			currency,
		]
	)

	return (
		<Box position="sticky" top="0" width="full">
			<Inline justifyContent={"between"}>
				<Inline gap="2" alignItems="center">
					<Inline
						borderXWidth={"1"}
						borderTopWidth={"1"}
						borderTopRadius="md"
						borderColor={"primary"}
						padding={"1"}
						color={"primary"}
						textAlign={"center"}
						gap={"2"}
					>
						<Icons.FlightTicket />
						{flightNumbers.length ? (
							<Text fontSize={"sm"} fontWeight="semibold">
								{joinAttributes(...flightNumbers)}
							</Text>
						) : null}
					</Inline>
				</Inline>
				<Button size="sm" inline onClick={() => showAddComments(true)}>
					<Icons.Annotation />
				</Button>
			</Inline>
			<Stack gap={"1"}>
				<Table fixedLayout roundedTopLeft="none" bordered>
					<thead>
						<tr>
							<TableHeaderDataCell style={{ borderTopLeftRadius: 0 }} />
							<TableHeaderDataCell borderXWidth="1">
								<Inline>
									<Text whiteSpace="nowrap">Cost Price </Text>
									<Box fontSize="xs" color="muted">
										(
										{allCurrencies?.length && (
											<InlineSelectInputField
												disabled={disableEditing}
												name={`${flightPricingName}.currency`}
												noCaret
											>
												{allCurrencies.map((o) => (
													<option value={o} key={o}>
														{o}
													</option>
												))}
											</InlineSelectInputField>
										)}
										)
									</Box>
								</Inline>
								<Divider marginY={"2"} />
								<CheckboxInputField
									label="Per Person"
									name={`${flightPricingName}.cost_per_person_enabled`}
									onChange={(e) => {
										const value = e.currentTarget.checked
										form.change(
											`${flightPricingName}.cost_per_person_enabled`,
											value ? 1 : 0
										)

										if (value) {
											distributePrice({
												field: "per_person_cost_price",
												totalPrice: calculatedPrice,
											})
										} else {
											form.change(
												`${flightPricingName}.per_person_cost_price`,
												undefined
											)
										}
									}}
								/>
							</TableHeaderDataCell>
							<TableHeaderDataCell>
								<Inline>
									<Text whiteSpace="nowrap">Selling Price </Text>
									<Box fontSize="xs" color="muted">
										(
										{allCurrencies?.length && (
											<InlineSelectInputField
												disabled={disableEditing}
												name={`${flightPricingName}.currency`}
												noCaret
											>
												{allCurrencies.map((o) => (
													<option value={o} key={o}>
														{o}
													</option>
												))}
											</InlineSelectInputField>
										)}
										)
									</Box>
								</Inline>
								<Divider marginY={"2"} />
								<CheckboxInputField
									name={`${flightPricingName}.given_per_person_enabled`}
									label="Per Person"
									onChange={(e) => {
										const value = e.currentTarget.checked
										form.change(
											`${flightPricingName}.given_per_person_enabled`,
											value ? 1 : 0
										)
										if (value) {
											distributePrice({
												field: "per_person_given_price",
												totalPrice: givenPrice,
											})
										} else {
											form.change(
												`${flightPricingName}.per_person_given_price`,
												undefined
											)
										}
									}}
								/>
							</TableHeaderDataCell>
						</tr>
					</thead>

					<tbody>
						<tr>
							<TableDataCell borderBottomWidth={"1"}>
								<TextInputField
									label="Adults"
									name={`${flightPricingName}.no_of_adults`}
									type="number"
									placeholder="1"
									min={1}
									max={maxPax.adults}
									disabled={disableEditing}
									onChange={(e) => {
										const { name, value } = e.currentTarget
										form.change(name, value === "" ? "" : Number(value))
									}}
									onBlur={(e) => {
										const { name, value } = e.currentTarget
										const parsed = parseInt(value, 10)
										if (isNaN(parsed)) {
											form.change(name, 1)
											return
										}

										form.change(name, Math.min(parsed, maxPax.adults))
									}}
								/>
							</TableDataCell>

							<TableDataCell
								borderBottomWidth={"1"}
								borderXWidth="1"
								verticalAlign="bottom"
							>
								{isPerPersonCostPrice && adults > 0 ? (
									<>
										<MoneyInputField
											name={`${flightPricingName}.per_person_cost_price.adult`}
											currencyFieldName={`${flightPricingName}.currency`}
											currencies={allCurrencies}
											disabled={disableEditing}
											width="full"
											required
										/>
										<Text fontSize="sm" color="muted">
											× {adults} = {totals.cost_price.adult}
										</Text>
									</>
								) : null}
							</TableDataCell>

							<TableDataCell borderBottomWidth={"1"} verticalAlign="bottom">
								{isPerPersonGivenPrice && adults > 0 ? (
									<>
										<MoneyInputField
											name={`${flightPricingName}.per_person_given_price.adult`}
											currencyFieldName={`${flightPricingName}.currency`}
											currencies={allCurrencies}
											disabled={disableEditing}
											width="full"
											required
										/>
										<Text fontSize="sm" color="muted">
											× {adults} = {totals.given_price.adult}
										</Text>
									</>
								) : null}
							</TableDataCell>
						</tr>

						<tr>
							<TableDataCell borderBottomWidth={"1"} verticalAlign="bottom">
								<TextInputField
									label={
										<Stack gap="px">
											<Text as="span" whiteSpace="nowrap">
												Children
											</Text>
											<Text fontSize="sm" color="muted" fontWeight="normal">
												({FLIGHT_INFANT_AGE_LIMIT} - {FLIGHT_CHILD_AGE_LIMIT}
												yrs)
											</Text>
										</Stack>
									}
									name={`${flightPricingName}.children`}
									type="number"
									placeholder="0"
									min={0}
									max={maxPax.children}
									disabled={disableEditing || maxPax.children === 0}
									onChange={(e) => {
										const { name, value } = e.currentTarget
										form.change(name, Number(value))
									}}
									onBlur={(e) => {
										const { name, value } = e.currentTarget
										const parsed = parseInt(value, 10)
										if (isNaN(parsed)) {
											form.change(name, 0)
											return
										}
										form.change(name, Math.min(parsed, maxPax.children))
									}}
								/>
							</TableDataCell>

							<TableDataCell
								borderBottomWidth={"1"}
								borderXWidth="1"
								verticalAlign="bottom"
							>
								{isPerPersonCostPrice && children > 0 ? (
									<>
										<MoneyInputField
											name={`${flightPricingName}.per_person_cost_price.child`}
											currencyFieldName={`${flightPricingName}.currency`}
											currencies={allCurrencies}
											disabled={disableEditing}
											width="full"
											required
										/>
										<Text fontSize="sm" color="muted">
											× {children} = {totals.cost_price.child}
										</Text>
									</>
								) : null}
							</TableDataCell>

							<TableDataCell borderBottomWidth={"1"} verticalAlign="bottom">
								{isPerPersonGivenPrice && children > 0 ? (
									<>
										<MoneyInputField
											name={`${flightPricingName}.per_person_given_price.child`}
											currencyFieldName={`${flightPricingName}.currency`}
											currencies={allCurrencies}
											disabled={disableEditing}
											width="full"
											required
										/>
										<Text fontSize="sm" color="muted">
											× {children} = {totals.given_price.child}
										</Text>
									</>
								) : null}
							</TableDataCell>
						</tr>

						<tr>
							<TableDataCell borderBottomWidth={"1"}>
								<TextInputField
									label={
										<Stack gap="px">
											<Text as="span" whiteSpace="nowrap">
												Infants
											</Text>
											<Text fontSize="sm" color="muted" fontWeight="normal">
												(below {FLIGHT_INFANT_AGE_LIMIT} yrs)
											</Text>
										</Stack>
									}
									name={`${flightPricingName}.infants`}
									type="number"
									placeholder="0"
									min={0}
									max={maxPax.infants}
									disabled={disableEditing || maxPax.infants === 0}
									onChange={(e) => {
										const { name, value } = e.currentTarget
										form.change(name, Number(value))
									}}
									onBlur={(e) => {
										const { name, value } = e.currentTarget
										const parsed = parseInt(value, 10)
										if (isNaN(parsed)) {
											form.change(name, 0)
											return
										}
										form.change(name, Math.min(parsed, maxPax.infants))
									}}
								/>
							</TableDataCell>

							<TableDataCell
								borderBottomWidth={"1"}
								borderXWidth="1"
								verticalAlign="bottom"
							>
								{isPerPersonCostPrice && infants > 0 ? (
									<>
										<MoneyInputField
											name={`${flightPricingName}.per_person_cost_price.infant`}
											currencyFieldName={`${flightPricingName}.currency`}
											currencies={allCurrencies}
											disabled={disableEditing}
											width="full"
											required
										/>
										<Text fontSize="sm" color="muted">
											× {infants} = {totals.cost_price.infant}
										</Text>
									</>
								) : null}
							</TableDataCell>

							<TableDataCell borderBottomWidth={"1"} verticalAlign="bottom">
								{isPerPersonGivenPrice && infants > 0 ? (
									<>
										<MoneyInputField
											name={`${flightPricingName}.per_person_given_price.infant`}
											currencyFieldName={`${flightPricingName}.currency`}
											currencies={allCurrencies}
											disabled={disableEditing}
											width="full"
											required
										/>
										<Text fontSize="sm" color="muted">
											× {infants} = {totals.given_price.infant}
										</Text>
									</>
								) : null}
							</TableDataCell>
						</tr>

						<tr>
							<TableDataCell verticalAlign="bottom">
								<Text fontWeight="semibold" fontSize={"md"}>
									Total
								</Text>
							</TableDataCell>

							<TableDataCell borderXWidth="1" verticalAlign="bottom">
								<MoneyInputField
									name={`${flightPricingName}.calculated_price`}
									currencyFieldName={`${flightPricingName}.currency`}
									currencies={allCurrencies}
									disabled={disableEditing || isPerPersonCostPrice}
									width="full"
								/>
							</TableDataCell>

							<TableDataCell verticalAlign="bottom">
								<MoneyInputField
									name={`${flightPricingName}.given_price`}
									currencyFieldName={`${flightPricingName}.currency`}
									currencies={allCurrencies}
									required
									disabled={disableEditing || isPerPersonGivenPrice}
									width="full"
								/>
							</TableDataCell>
						</tr>
					</tbody>
				</Table>
				{commentsVisible && (
					<TextInputField
						type="text"
						name={`${flightPricingName}.comments`}
						label="Comments"
						secondaryLabel="Internal"
						placeholder="Any comments regarding service"
						disabled={disableEditing}
					/>
				)}
				{adjustedPaxTypes.length > 0 && (
					<Alert inline status="warning">
						<Inline flexWrap={"wrap"} gap={"1"}>
							<Text textTransform="capitalize">
								{formatPaxTypesList(adjustedPaxTypes)}
							</Text>
							<Text>count was updated to match the quote.</Text>
						</Inline>
					</Alert>
				)}
			</Stack>
			<DeferRender by={500} noLoader>
				<SyncPerPersonPricing
					name={flightPricingName}
					adults={adults}
					children={children}
					infants={infants}
					perPersonCostPrice={perPersonCostPrice}
					perPersonGivenPrice={perPersonGivenPrice}
					isPerPersonCostPrice={isPerPersonCostPrice}
					isPerPersonGivenPrice={isPerPersonGivenPrice}
					maxPax={maxPax}
					onPaxAdjusted={setAdjustedPaxTypes}
				/>
			</DeferRender>
		</Box>
	)
}

function ScheduleInputFields({
	bookingFrom,
	name,
	groupName,
	index,
	onRemove,
	onAddingGroup,
	onAddingSchedule,
	schedulesLength,
}: {
	bookingFrom?: string
	name: string
	groupName: string
	index: number
	onRemove?: (index: number) => void
	onAddingSchedule?: (
		scheduler: Required<IFlightServicesParams>["flights"][number]["groups"][number]["schedules"][number]
	) => void
	onAddingGroup?: (
		group: Required<IFlightServicesParams>["flights"][number]["groups"][number]
	) => void
	schedulesLength?: number
}) {
	const { value: flightType } =
		useFieldValue<IFlightServicesParams["flight_type"]>("flight_type")
	const removeSchedule = useCallback(() => onRemove?.(index), [onRemove, index])
	const addSchedule = useCallback(() => {
		flightType === "MULTI_CITY"
			? onAddingGroup?.({
					type: "MULTI_CITY",
					schedules: [
						// schedule will be multiple in case of connecting flights (eg: main case for oneway)
						{
							source: undefined,
							destination: undefined,
							departs_at: undefined,
							arrives_at: undefined,
							airline: undefined,
						},
					],
				})
			: onAddingSchedule?.({
					source: undefined,
					destination: undefined,
					departs_at: undefined,
					arrives_at: undefined,
					airline: undefined,
				})
	}, [onAddingGroup, onAddingSchedule, flightType])
	return (
		<Stack gap={"4"}>
			<Inline gap={"2"} collapseBelow="md">
				<Stack gap={"2"}>
					<Inline gap={"2"} flexWrap={"wrap"}>
						<SelectField
							select={SelectAirports}
							label="Departure Airport"
							name={`${name}.source`}
							creatable={true}
						/>

						<TextInputField
							name={`${name}.departure_terminal`}
							label="Terminal"
							placeholder="eg. T1"
							type="text"
							max={10}
							min={1}
							style={{ maxWidth: "100px" }}
						/>

						<Box
							style={{
								minWidth: "210px",
							}}
						>
							<DatePickerField
								label="Departure Time"
								name={`${name}.departs_at_local`}
								timeFormat="HH:mm"
								dateFormat={"ddd, DD MMM YY"}
							/>
						</Box>
					</Inline>

					<Inline gap={"2"} flexWrap={"wrap"}>
						<SelectField
							select={SelectAirports}
							label="Arrival Airport"
							name={`${name}.destination`}
							creatable={true}
						/>

						<TextInputField
							name={`${name}.arrival_terminal`}
							label="Terminal"
							placeholder="eg. T2"
							type="text"
							max={10}
							min={1}
							style={{ maxWidth: "100px" }}
						/>
						<GetFieldValue<string | Date> name={`${name}.departs_at_local`}>
							{({ value }) => {
								const minDate = value
									? subtractUnit(value, 1, "day")
									: undefined
								return (
									<Box
										style={{
											minWidth: "210px",
										}}
									>
										<DatePickerField
											label="Arrival Time"
											name={`${name}.arrives_at_local`}
											timeFormat="HH:mm"
											dateFormat={"ddd, DD MMM YY"}
											min={minDate}
											isValidDate={(date) =>
												minDate ? isSameOrAfter(date, minDate, "day") : false
											}
										/>
									</Box>
								)
							}}
						</GetFieldValue>
					</Inline>
					<Box style={{ maxWidth: "150px" }}>
						<SelectField
							select={SelectAirlines}
							label="Flight Number"
							name={`${name}.airline`}
							creatable
						/>
					</Box>
				</Stack>

				<Inline paddingTop={"6"} gap={"2"}>
					{schedulesLength && schedulesLength === index + 1 ? (
						<Button status="primary" onClick={addSchedule}>
							<Icons.Plus />
						</Button>
					) : null}

					{(index || 0) > 0 ? (
						<Button onClick={removeSchedule}>
							<Icons.Cancel />
						</Button>
					) : null}
				</Inline>
			</Inline>
			<GetFieldValue<
				Required<IFlightServicesParams>["flights"][number]["groups"][number]
			>
				name={`${groupName}`}
			>
				{({ value: groupDetails }) => {
					// if the flight has multiple schedules
					if (
						groupDetails.schedules.length > 1 &&
						groupDetails.schedules.length !== index + 1
					) {
						const layoverCity =
							groupDetails.schedules[index].destination?.city?.name ||
							groupDetails.schedules[index].destination?.iata

						const arrivalDate =
							groupDetails.schedules[index]?.arrives_at_local || ""
						const depDate =
							groupDetails.schedules[index + 1]?.departs_at_local || ""

						return (
							<Inline justifyContent={"center"} paddingBottom={"4"}>
								<Box
									borderColor={"primary"}
									borderWidth={"1"}
									paddingY={"2"}
									paddingX={"4"}
									rounded="full"
									style={{ borderStyle: "dashed" }}
								>
									<Text>
										{humanizeDuration(
											getDiff(arrivalDate as string, depDate as string)
										)}{" "}
										Layover in {layoverCity}
									</Text>
								</Box>
							</Inline>
						)
					}
					return null
				}}
			</GetFieldValue>
		</Stack>
	)
}

function AdditionalScheduleInputField({
	name,
	flightName,
}: {
	name: string
	flightName: string
}) {
	const { value: infants } = useFieldValue<number>(
		`${flightName}.pricing.infants`
	)
	return (
		<Stack gap="4" width={"full"}>
			<GetFieldValue<Array<{ id: number; value: string }>>
				name={`${name}.additional_options`}
			>
				{({ value: fields }) => {
					const additionalOptions = (fields || []).map((field) => field.value)
					return (
						<Stack gap={"2"} width={"full"}>
							<SelectField
								select={Select}
								label={
									<Inline gap={"2"} alignItems={"center"}>
										<Icons.Users /> <Text>Travel Class</Text>
									</Inline>
								}
								name={`${name}.info.category_class`}
								placeholder="Economy"
								style={{ maxWidth: "150px" }}
								options={FLIGHT_CATEGORY_CLASSES}
							/>
							{additionalOptions.includes("baggage") ? (
								<>
									<Inline gap={"2"}>
										<SelectField
											label={
												<Inline gap={"2"} alignItems={"center"}>
													<Icons.Baggage /> <Text>Cabin Baggage</Text>
												</Inline>
											}
											fetchOnMount
											select={SelectAdditionalFlightInfo}
											name={`${name}.info.adult_hand_baggage`}
											infoKey="adult_hand_baggage"
											placeholder="Select baggages"
											style={{ maxWidth: "150px" }}
										/>
										<SelectField
											label={
												<Inline gap={"2"} alignItems={"center"}>
													<Icons.Baggage /> <Text>Check In Baggage</Text>
												</Inline>
											}
											fetchOnMount
											name={`${name}.info.adult_check_in_baggage`}
											infoKey="adult_check_in_baggage"
											select={SelectAdditionalFlightInfo}
											style={{ maxWidth: "150px" }}
											placeholder="Select baggages"
										/>
									</Inline>
									{infants > 0 ? (
										<Inline gap={"2"}>
											<SelectField
												label={
													<Inline gap={"2"} alignItems={"center"}>
														<Icons.Baggage /> <Text>Cabin Baggage</Text>
													</Inline>
												}
												fetchOnMount
												secondaryLabel={"Infants"}
												select={SelectAdditionalFlightInfo}
												name={`${name}.info.infant_hand_baggage`}
												placeholder="Select baggages"
												infoKey="infant_hand_baggage"
											/>
											<SelectField
												label={
													<Inline gap={"2"} alignItems={"center"}>
														<Icons.Baggage /> <Text>Check In Baggage</Text>
													</Inline>
												}
												fetchOnMount
												secondaryLabel={"Infants"}
												name={`${name}.info.infant_check_in_baggage`}
												select={SelectAdditionalFlightInfo}
												placeholder="Select baggage"
												infoKey="infant_check_in_baggage"
											/>
										</Inline>
									) : null}
								</>
							) : null}

							<Inline gap={"2"}>
								{additionalOptions.includes("fare") ? (
									<SelectField
										label={
											<Inline gap={"2"} alignItems={"center"}>
												<Icons.Ticket /> <Text>Fare Type</Text>
											</Inline>
										}
										fetchOnMount
										name={`${name}.info.fare_type`}
										select={SelectAdditionalFlightInfo}
										infoKey="fare_type"
										placeholder="Select fare type"
										style={{ maxWidth: "150px" }}
									/>
								) : null}
								{additionalOptions.includes("meal") ? (
									<SelectField
										label={
											<Inline gap={"2"} alignItems={"center"}>
												<Icons.Meal /> <Text>Meals</Text>
											</Inline>
										}
										fetchOnMount
										select={SelectAdditionalFlightInfo}
										placeholder="Select Meal Preference"
										infoKey="meal_preference"
										name={`${name}.info.meal_preference`}
										style={{ maxWidth: "150px" }}
									/>
								) : null}
							</Inline>

							{additionalOptions.includes("notes_customer") ? (
								<Stack>
									<Inline
										gap={"2"}
										alignItems={"center"}
										as="label"
										htmlFor={`${name}.remarks`}
									>
										<Icons.Annotation /> <Text>Notes for Customers</Text>
									</Inline>
									<TextAreaInputField
										type="text"
										name={`${name}.remarks`}
										width={"full"}
										placeholder="Any comments regarding service"
										rowSpan={2}
									/>
								</Stack>
							) : null}
						</Stack>
					)
				}}
			</GetFieldValue>
		</Stack>
	)
}

/**
 * preview
 */

export function PreviewFlightOnDialog({
	flights,
	children,
}: {
	flights: Array<{ groups: Array<IPreviewFlightData>; pricing: IFlightPricing }>
	children: (props: { open: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ open: () => open() })}
			<Dialog open={isOpen} onClose={close} title="Flight Preview">
				<Dialog.Body>
					<FlightPreview flightsData={flights} />
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function FormFlightPreview({ name }: { name: string }) {
	const { value: flightsFormData } =
		useFieldValue<Required<IFlightServicesParams>["flights"][number]>(name)
	const flightsData = useMemo(
		() => transformFlightsFormDataToPreviewData([flightsFormData]),
		[flightsFormData]
	)
	return <FlightPreview flightsData={flightsData} />
}

function FlightPreview({
	flightsData,
}: {
	flightsData: Array<{
		groups: Array<IPreviewFlightData>
		pricing: IFlightPricing
	}>
}) {
	return (
		<Stack gap="4">
			{flightsData.map(({ groups: flightGroup }) =>
				flightGroup.map((flight, index) => (
					<Stack key={index} alignItems={"center"} gap="4">
						<Inline collapseBelow="md" width={"full"}>
							{flight ? (
								<Inline gap={"4"} collapseBelow="md" width={"full"}>
									<Box
										rounded="md"
										borderWidth={"1"}
										borderColor={"muted"}
										boxShadow={"lg"}
										width={"full"}
										style={{ backgroundColor: "#EDF2FE33" }} // support for dark mode
									>
										<FlightHeading
											from={flight.from}
											to={flight.to}
											flightNumbers={flight.flight_number}
											typeOfFlight={flight.type_of_flight}
											numberOfFlight={flight.no_flight}
											via={flight.via}
										/>
										{/* schedule */}
										{flight.flight_details.map(
											({ departure, arrival, duration_formatted }, idx) => (
												<Box padding={"4"} key={idx}>
													<Grid>
														<Col xs={12} sm={5} md={5} lg={5}>
															{departure?.airport ? (
																<ScheduleDetails schedule={departure} />
															) : null}
														</Col>
														<Col xs={12} sm={2} md={2} lg={2}>
															<Stack alignItems={"center"} paddingTop={"6"}>
																<Text fontWeight={"normal"} fontSize={"sm"}>
																	{duration_formatted}
																</Text>
																<Icons.ClockSolid size="6" />
															</Stack>
														</Col>
														<Col xs={12} sm={5} md={5} lg={5}>
															{arrival?.airport ? (
																<ScheduleDetails schedule={arrival} />
															) : null}
														</Col>
													</Grid>
													<Divider sm />

													{idx < flight.flight_details.length - 1 ? (
														<>
															{flight.type_of_flight !== "Multi City" &&
															flight.flight_details[idx + 1]?.departure
																?.schedule_date &&
															arrival?.schedule_date ? (
																<Inline
																	justifyContent={"center"}
																	paddingTop={"4"}
																>
																	<Box
																		borderColor={"primary"}
																		borderWidth={"1"}
																		backgroundColor={"white"}
																		paddingY={"2"}
																		paddingX={"4"}
																		rounded="full"
																		style={{ borderStyle: "dashed" }}
																	>
																		<Text>
																			{humanizeDuration(
																				getDiff(
																					arrival.schedule_date,
																					flight.flight_details[idx + 1]
																						?.departure?.schedule_date as string
																				)
																			)}{" "}
																			Layover in{" "}
																			{arrival.airport?.city?.name ||
																				arrival.airport?.iata ||
																				""}
																		</Text>
																	</Box>
																</Inline>
															) : null}
														</>
													) : null}
												</Box>
											)
										)}
										<Stack gap={"2"} paddingX={"4"} paddingBottom={"2"}>
											<Inline gap={"2"}>
												<Text fontWeight={"semibold"}>Class:</Text>
												<Text>{flight.info?.category_class || "Economy"}</Text>
											</Inline>
											<Inline gap={"2"} flexWrap={"wrap"}>
												<Inline gap={"2"}>
													<Text fontWeight={"semibold"}>Cabin Baggage:</Text>
													<Text>
														{flight.info?.adult_hand_baggage || "N/A"}
													</Text>
												</Inline>

												<Inline gap={"2"}>
													<Text fontWeight={"semibold"}>Check In Baggage:</Text>
													<Text>
														{flight.info?.adult_check_in_baggage || "N/A"}
													</Text>
												</Inline>

												<Inline gap={"2"}>
													<Text fontWeight={"semibold"}>
														Cabin Baggage (Infant):
													</Text>
													<Text>
														{flight.info?.infant_hand_baggage || "N/A"}
													</Text>
												</Inline>

												<Inline gap={"2"}>
													<Text fontWeight={"semibold"}>
														Check In Baggage (Infant):
													</Text>
													<Text>
														{flight.info?.infant_check_in_baggage || "N/A"}
													</Text>
												</Inline>

												{flight.info?.meal_preference ? (
													<Inline gap={"2"}>
														<Text fontWeight={"semibold"}>
															Meal Preference:
														</Text>
														<Text>{flight.info.meal_preference}</Text>
													</Inline>
												) : null}
												{flight.info?.fare_type ? (
													<Inline gap={"2"}>
														<Text fontWeight={"semibold"}>Fare: </Text>
														<Text>{flight.info.fare_type}</Text>
													</Inline>
												) : null}
											</Inline>
											{flight.remarks ? (
												<Inline gap={"2"}>
													<Text fontWeight={"semibold"}>
														Notes for customer:
													</Text>
													<Text>{flight.remarks}</Text>
												</Inline>
											) : null}
										</Stack>
									</Box>
								</Inline>
							) : null}
						</Inline>
						{index < flightGroup.length - 1 &&
						flight.type_of_flight !== "One Way" ? (
							<Box
								display={"flex"}
								alignItems={"center"}
								justifyContent={"center"}
								rounded="full"
								size="12"
								borderWidth={"1"}
								borderColor={"muted"}
								backgroundColor={"primary"}
							>
								{flight.type_of_flight === "Return" ||
								flight.type_of_flight === "Onward" ? (
									<Icons.SwitchHorizontal rotate="90" />
								) : (
									<Icons.ArrowLeft rotate="270" />
								)}
							</Box>
						) : null}
					</Stack>
				))
			)}
		</Stack>
	)
}

function FlightHeading({
	from,
	to,
	flightNumbers,
	typeOfFlight,
	numberOfFlight,
	via,
}: {
	from: string
	to: string
	flightNumbers: string[]
	typeOfFlight: string
	numberOfFlight: string | number
	via: string[]
}) {
	return (
		<Inline
			justifyContent={"between"}
			alignItems={"center"}
			padding={"4"}
			borderBottomWidth={"1"}
			borderColor={"muted"}
			flexWrap={"wrap"}
			collapseBelow="sm"
			gap={"2"}
		>
			<Inline gap={"2"} alignItems={"center"} flexWrap={"wrap"}>
				<SectionIcon>
					<Icons.Airplane size="4" />
				</SectionIcon>

				<Text fontWeight={"semibold"} fontSize={"xl"}>
					{from}
				</Text>
				<Box position={"relative"}>
					<Box width={"20"} borderWidth={"1"} borderColor={"black"} />
					{via.length > 0 ? (
						<Box position={"absolute"} width={"full"} paddingTop={"1"}>
							<Text fontSize={"xs"} textAlign={"center"} color={"muted"}>
								via {via.map((v) => v).join(", ")}
							</Text>
						</Box>
					) : null}
				</Box>

				<Text fontWeight={"semibold"} fontSize={"xl"}>
					{to}
				</Text>

				<Box bgColor={"emphasis"} size={"2"} rounded="full" />
				<Badge outlined primary>
					<Inline gap={"1"} alignItems={"center"}>
						<Icons.Ticket />
						<Text fontWeight="semibold">{flightNumbers.join(" • ")}</Text>
					</Inline>
				</Badge>
			</Inline>
			<Inline gap={"2"}>
				<Badge accent>{typeOfFlight}</Badge>
				<Badge success>{numberOfFlight}</Badge>
			</Inline>
		</Inline>
	)
}

function ScheduleDetails({ schedule }: { schedule: IFlightAirPort }) {
	return (
		<Stack>
			<Box
				borderBottomWidth={"2"}
				borderColor={"muted"}
				paddingBottom={"2"}
				fontWeight="normal"
			>
				<Inline
					alignItems={"center"}
					gap={"2"}
					fontWeight={"semibold"}
					fontSize={"lg"}
					flexWrap={"wrap"}
				>
					<Text>
						{schedule.airport?.city?.name ||
							schedule.airport?.iata ||
							schedule.airport?.name ||
							" "}
					</Text>
					{schedule.schedule_date_local ? (
						<Time
							value={schedule.schedule_date_local}
							timeFormat="HH:mm"
							fontWeight={"normal"}
						/>
					) : null}
				</Inline>
				{schedule.schedule_date_local ? (
					<Time
						value={schedule.schedule_date_local}
						timeFormat="ddd, DD MMMM YYYY"
						fontWeight={"normal"}
					/>
				) : null}
			</Box>
			<Box>
				<Inline gap={"1"} alignItems={"start"}>
					<Box paddingRight={"2"}>
						<Text fontWeight={"semibold"} fontSize={"lg"}>
							{schedule.airport?.iata}
						</Text>
					</Box>
					<Stack
						fontWeight={"normal"}
						fontSize={"sm"}
						color={"muted"}
						gap={"1"}
						paddingTop={"1"}
					>
						<Text>{schedule.airport?.name}</Text>
						<Box>
							<Inline gap={"2"} flexWrap={"wrap"}>
								{schedule.terminal ? (
									<Inline
										gap={"1"}
										alignItems={"center"}
										borderRightWidth={"1"}
										paddingRight={"2"}
									>
										<Icons.Building />
										<Text>Terminal {schedule.terminal}</Text>
									</Inline>
								) : null}
								{schedule.airport?.timezone?.name ? (
									<Inline gap={"1"} alignItems={"center"}>
										<Icons.Global />
										<Text>
											{getTimezoneOffset(
												schedule.schedule_date_local,
												schedule.airport?.timezone?.name
											)}
										</Text>
									</Inline>
								) : null}
							</Inline>
						</Box>
					</Stack>
				</Inline>
			</Box>
		</Stack>
	)
}

function BoxBadge({
	title,
	children,
	icon,
}: {
	title: string
	children: React.ReactNode
	icon?: React.ReactNode
}) {
	return (
		<Box
			position={"relative"}
			paddingX={"2"}
			paddingY={"4"}
			borderRadius="md"
			borderWidth={"1"}
			borderColor={"muted"}
			width={"full"}
		>
			<Inline
				position={"absolute"}
				borderRadius="lg"
				borderWidth={"1"}
				borderColor={"muted"}
				backgroundColor={"primary"}
				paddingX={"1"}
				style={{
					top: "-15px",
				}}
				alignItems={"center"}
				gap={"1"}
			>
				{icon ? icon : null}
				{title}
			</Inline>
			{children}
		</Box>
	)
}
